import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import {
  addFinalEvaluator,
  allApproved,
  approveFinalDelivery,
  removeFinalEvaluator,
} from "../../../../../api/finalDelivery";
import { formatDate } from "../../../../../utils/getEntitiesUtils";

/**
 * Evaluadores de la entrega final de un equipo: de 1 a N, tutores del sistema
 * o externos con nombre y mail.
 *
 * El evaluador que asignó el algoritmo de fechas aparece marcado como "de la
 * exposición" y no se puede sacar desde acá: se cambia reasignando la fecha.
 * Cada alta o baja se guarda en el momento; `onChange` recibe la lista nueva.
 *
 * La entrega queda aprobada cuando aprobaron todos. Desde acá el admin aprueba
 * en nombre de un evaluador: es la única forma de que lo haga un externo, que
 * no tiene cuenta. Un evaluador nuevo arranca pendiente.
 */
const FinalEvaluatorsDialog = ({ open, team, evaluators, tutors, onClose, onChange }) => {
  const user = useSelector((state) => state.user);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [externalName, setExternalName] = useState("");
  const [externalEmail, setExternalEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const current = evaluators || [];
  const assignedUserIds = new Set(current.map((e) => e.user_id).filter(Boolean));
  const options = tutors.filter((t) => !assignedUserIds.has(t.id));

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo guardar. Inténtalo de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddTutor = () =>
    run(async () => {
      const added = await addFinalEvaluator(user, team.id, { user_id: selectedTutor.id });
      onChange(team.id, [...current, added]);
      setSelectedTutor(null);
    });

  const handleAddExternal = () =>
    run(async () => {
      const added = await addFinalEvaluator(user, team.id, {
        name: externalName.trim(),
        email: externalEmail.trim(),
      });
      onChange(team.id, [...current, added]);
      setExternalName("");
      setExternalEmail("");
    });

  const handleApprove = (evaluator) =>
    run(async () => {
      const result = await approveFinalDelivery(user, team.id, evaluator.id);
      onChange(team.id, result.evaluators);
    });

  const handleRemove = (evaluator) =>
    run(async () => {
      await removeFinalEvaluator(user, team.id, evaluator.id);
      onChange(
        team.id,
        current.filter((e) => e.id !== evaluator.id)
      );
    });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Evaluadores de la entrega final
        {team && (
          <Typography variant="subtitle1" color="text.secondary">
            Equipo {team.group_number}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {current.length > 0 && (
          <Alert severity={allApproved(current) ? "success" : "info"} sx={{ mb: 2 }}>
            {allApproved(current)
              ? "Entrega final aprobada por todos los evaluadores."
              : `Aprobaron ${current.filter((e) => e.approved_at).length} de ${
                  current.length
                }. Queda aprobada cuando aprueben todos.`}
            {!team?.final_report_date && " El equipo todavía no completó la entrega."}
          </Alert>
        )}

        {current.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Todavía no tiene evaluadores. Hace falta al menos uno para mandar el aviso.
          </Typography>
        ) : (
          <List dense disablePadding>
            {current.map((evaluator) => (
              <ListItem
                key={evaluator.id}
                disableGutters
                secondaryAction={
                  <Tooltip
                    title={
                      evaluator.is_exhibition_evaluator
                        ? "Es el evaluador de la fecha de exposición: se cambia reasignando la fecha"
                        : "Sacar"
                    }
                  >
                    <span>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemove(evaluator)}
                        disabled={busy || evaluator.is_exhibition_evaluator}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{evaluator.name}</span>
                      {evaluator.is_external && <Chip label="Externo" size="small" />}
                      {evaluator.is_exhibition_evaluator && (
                        <Chip label="Exposición" size="small" color="primary" variant="outlined" />
                      )}
                    </Stack>
                  }
                  secondary={
                    <>
                      {evaluator.email}
                      <br />
                      {evaluator.approved_at ? (
                        <Box component="span" sx={{ color: "success.main" }}>
                          <CheckCircleIcon
                            fontSize="inherit"
                            sx={{ verticalAlign: "middle", mr: 0.5 }}
                          />
                          Aprobó el {formatDate(evaluator.approved_at)}
                          {evaluator.approved_by &&
                            evaluator.approved_by !== evaluator.name &&
                            ` (registrado por ${evaluator.approved_by})`}
                          {evaluator.approval_comment && `: “${evaluator.approval_comment}”`}
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => handleApprove(evaluator)}
                          disabled={busy || !team?.final_report_date}
                          sx={{ textTransform: "none", px: 0, minWidth: 0 }}
                        >
                          Aprobar en su nombre
                        </Button>
                      )}
                    </>
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Agregar un tutor
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Autocomplete
            sx={{ flex: 1 }}
            size="small"
            options={options}
            value={selectedTutor}
            onChange={(_, value) => setSelectedTutor(value)}
            getOptionLabel={(t) => `${t.name} ${t.last_name}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} placeholder="Buscar tutor" />}
          />
          <Button
            variant="outlined"
            onClick={handleAddTutor}
            disabled={!selectedTutor || busy}
          >
            Agregar
          </Button>
        </Stack>

        <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
          Agregar a alguien sin cuenta
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Se le crea una cuenta de tutor con ese mail (si ya existe, se usa esa). No recibe
          ningún mail de alta: para entrar usa "¿Olvidaste tu contraseña?".
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Nombre y apellido"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Mail"
            type="email"
            value={externalEmail}
            onChange={(e) => setExternalEmail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleAddExternal}
            disabled={!externalName.trim() || !externalEmail.trim() || busy}
          >
            Agregar
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        {busy && (
          <Box sx={{ mr: "auto", ml: 2 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalEvaluatorsDialog;
